import React, { useCallback, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'

import UploadedFilesField from '../../../../../../../../features/components/UploadedFilesField'

import { getFileIdsForBulkUpdate } from './helpers'
import { useOpenMediaOrderSummaryInNewTab } from '../hooks'
import {
  BUYER_DISAPPROVED,
  DISAPPROVED,
  getApprovalActionStatus,
  getStatusForDisapprovalAction
} from '../../../../../../../../features/components/UploadedFilesField/helpers'
import { getMediaOrderOrMediaOrderGroupSelfAccountId } from '../helpers'

import { bulkUpdateBookingMediaFile } from '../../../../../../../../modules/actions/mediaOrdersBookings'
import {
  isControllerBuyerApprovalSelector,
  selectedControllerIdSelector
} from '../../../../../../../../modules/selectors/app'
import { getBookedMediaSelector } from '../../../../../../../../modules/selectors/mediaOrdersBookings'
import {
  BUYER_DISAPPROVED_REASON,
  DISAPPROVED_REASON
} from '../../../../../../../../features/components/Modals/AskReasonModal/fields'

import useStyles from './styles'

const UploadedFilesCell = ({ installationReportRowData, Context }) => {
  const {
    uploaded_files: uploadedFiles,
    sequential_ids: sequentialIds,
    sequential_list: sequentialList,
    id,
    media_order: mediaOrderId,
    account_id: accountId,
    additional_recipients: additionalRecipients
  } = installationReportRowData

  const selectedControllerId = useSelector(selectedControllerIdSelector)
  const { filterRequestParams } = useContext(Context)

  const firstSequentialId = sequentialList?.[0]?.id
  const bookedMediaId = firstSequentialId || id
  const selfAccountId = getMediaOrderOrMediaOrderGroupSelfAccountId(accountId, sequentialList)

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const classes = useStyles()

  const isSubRow = useMemo(() => !sequentialIds && id, [sequentialIds, id])

  const isControllerBuyerApproval = useSelector(isControllerBuyerApprovalSelector)
  const bookedMediaData = useSelector(getBookedMediaSelector)

  const onApproveHandler = useCallback(
    ({ uploadedFile }) => {
      const ids = getFileIdsForBulkUpdate({ uploadedFile, uploadedFiles })

      const sentryData = {
        bookedMediaData,
        installationReportRowData
      }

      const bulkUpdateBookingMediaFileData = {
        ids,
        approval_status: getApprovalActionStatus(isControllerBuyerApproval, uploadedFile),
        booked_media: bookedMediaId,
        sentryData,
        sequentialIds
      }

      dispatch(
        bulkUpdateBookingMediaFile(bulkUpdateBookingMediaFileData, {
          ...filterRequestParams,
          media_order_id: mediaOrderId,
          controller: selectedControllerId
        })
      )
    },
    [
      dispatch,
      isControllerBuyerApproval,
      sequentialIds,
      installationReportRowData,
      bookedMediaData,
      uploadedFiles,
      bookedMediaId,
      filterRequestParams,
      mediaOrderId,
      selectedControllerId
    ]
  )

  const onDisapproveHandler = useCallback(
    ({ uploadedFile, [DISAPPROVED_REASON]: disapprovalReason }) => {
      const ids = getFileIdsForBulkUpdate({ uploadedFile, uploadedFiles })

      const disapprovalStatus = getStatusForDisapprovalAction(uploadedFile, isControllerBuyerApproval)

      const sentryData = {
        bookedMediaData,
        installationReportRowData
      }

      const bulkUpdateBookingMediaFileData = {
        ids,
        approval_status: disapprovalStatus,
        ...(disapprovalReason && disapprovalStatus === DISAPPROVED && { [DISAPPROVED_REASON]: disapprovalReason }),
        ...(disapprovalReason &&
          disapprovalStatus === BUYER_DISAPPROVED && { [BUYER_DISAPPROVED_REASON]: disapprovalReason }),
        booked_media: bookedMediaId,
        sentryData,
        sequentialIds
      }

      dispatch(
        bulkUpdateBookingMediaFile(bulkUpdateBookingMediaFileData, {
          ...filterRequestParams,
          media_order_id: mediaOrderId,
          controller: selectedControllerId
        })
      )
    },
    [
      dispatch,
      isControllerBuyerApproval,
      sequentialIds,
      installationReportRowData,
      bookedMediaData,
      uploadedFiles,
      bookedMediaId,
      filterRequestParams,
      mediaOrderId,
      selectedControllerId
    ]
  )

  if (!accountId) {
    const selfAccountMissingError = new Error('Installation report UploadedFiles cell self account id is missing')
    Sentry.captureException(selfAccountMissingError, {
      extra: {
        installationReportRowData: JSON.stringify(installationReportRowData, null, 2)
      }
    })
  }
  const goToMediaOrderSummaryHandler = useOpenMediaOrderSummaryInNewTab({ mediaOrderId, accountId: selfAccountId })

  if (!uploadedFiles?.length && (additionalRecipients || sequentialList?.[0].additional_recipients)) {
    return <div className={classes.requested}>{t('Requested')}</div>
  }

  if (
    !uploadedFiles?.length ||
    // don't show files on sub rows
    isSubRow
  ) {
    return null
  }

  return (
    <UploadedFilesField
      uploadedFiles={uploadedFiles}
      bookedMediaId={bookedMediaId}
      onApproveHandler={onApproveHandler}
      onDisapproveHandler={onDisapproveHandler}
      goToMediaOrderSummaryHandler={goToMediaOrderSummaryHandler}
      installationReportRowData={installationReportRowData}
    />
  )
}

UploadedFilesCell.propTypes = {
  installationReportRowData: PropTypes.shape({
    uploaded_files: PropTypes.array,
    sequential_ids: PropTypes.array,
    sequential_list: PropTypes.array,
    id: PropTypes.number,
    media_order: PropTypes.number,
    account_id: PropTypes.number,
    additional_recipients: PropTypes.array
  }).isRequired,
  installationReportFilters: PropTypes.object.isRequired
}

export default UploadedFilesCell
